import { Link } from 'react-router-dom';
import { usePublicConfig } from '../PublicShell';
import { LegalLayout, Section, List } from './LegalLayout';

export default function Terms() {
  const c = usePublicConfig();
  const company = c.company.name;
  const product = c.company.product;
  const starter = c.pricing.plans[0];
  const standard = c.pricing.plans[1];

  return (
    <LegalLayout
      title="Terms of service"
      updated="11 September 2026"
      intro={(
        <>
          These terms govern your use of {product}, the software. Your contract is with{' '}
          {company}, the company that builds and operates it — {product} is the product,{' '}
          {company} is the provider, and it is {company} you pay, write to and can hold to
          these terms.
        </>
      )}
    >
      <Section title="1. The agreement">
        <p>
          By creating an account you agree to these terms, to the{' '}
          <Link to="/privacy">privacy policy</Link> and to the{' '}
          <Link to="/data-processing">data processing agreement</Link>. If you are agreeing on
          behalf of a company, you confirm you are authorised to do so.
        </p>
        <p>These terms are for business use. {product} is not a consumer product.</p>
      </Section>

      <Section title="2. What we provide">
        <p>
          {product} is property management software: properties and rooms, tenants and
          tenancies, a rent ledger with a verification step, utility bills, document storage,
          text messaging and email, a tenant portal, tenancy agreements your tenants can sign
          in that portal, reminders, and tools for bringing your records in from elsewhere and
          taking them out again.
        </p>
        <p>
          We aim to keep the service available at all times but do not guarantee uninterrupted
          access. We may carry out maintenance, and will give notice of planned downtime where
          we reasonably can.
        </p>
        <p>
          <strong className="text-ink">{product} is a record-keeping tool, not financial,
          legal or tax advice.</strong> It does not verify that a payment was really received,
          collect money on your behalf, or check that your tenancies comply with the law. Those
          judgements remain yours.
        </p>
      </Section>

      <Section title="3. Plans and pricing">
        <List
          items={[
            <><strong className="text-ink">{starter.name}</strong> — {starter.price_display} per
              month, up to {starter.property_limit} properties.</>,
            <><strong className="text-ink">{standard.name}</strong> — {standard.price_display} per
              month, up to {standard.property_limit} properties.</>,
            <><strong className="text-ink">Custom</strong> — above {standard.property_limit}{' '}
              properties, priced by agreement. Contact{' '}
              <a href={`mailto:${c.contact.sales}`}>{c.contact.sales}</a>.</>,
          ]}
        />
        <p>
          Prices are in pounds sterling and exclude VAT where it applies. Fees are billed
          monthly in advance and are not usage-metered: <strong className="text-ink">text
          messages are included in the monthly price</strong>, with no per-message charge and
          no overage invoice.
        </p>
        <p>
          If your usage becomes sustained and materially disproportionate to your plan — many
          times what a portfolio of your size would normally send — we may contact you to agree
          a different plan. We will not issue an unexpected larger invoice, and we will give you
          at least 30 days&rsquo; notice before any price change takes effect.
        </p>
        <p>
          We may change published prices with at least 30 days&rsquo; notice by email. If you do
          not accept a change you may cancel before it takes effect.
        </p>
      </Section>

      <Section title="4. Your responsibilities">
        <List
          items={[
            'Keeping your login credentials secure, and telling us promptly if you think an account has been compromised.',
            'The accuracy of what you enter, and having a lawful basis for holding the personal data you upload.',
            'Telling your tenants how their data is used, as data protection law requires of you as controller.',
            'Obtaining consent where required before sending marketing messages to tenants.',
            'Everything done under your account, including by staff you invite.',
            'The content of every message sent from your account — including messages sent in your organisation’s name to your tenants.',
          ]}
        />
      </Section>

      <Section title="5. Complying with the law where you let">
        <p>
          {product} is a record-keeping tool. It is <strong>not legal advice</strong>, and it
          does not know the rules that apply where you let. Complying with housing,
          tenancy, licensing, safety and tax law in your own country — and often in your own
          local authority area — is entirely your responsibility.
        </p>
        <p>That includes, without limitation:</p>
        <List
          items={[
            'Protecting deposits in an approved scheme where that is required, within the time limits that apply.',
            'Right-to-rent, immigration and identity checks where your jurisdiction requires them, and doing them without discriminating.',
            'Gas, electrical, fire and other safety certificates, and giving tenants the documents they are entitled to.',
            'Any licence or registration your property or your business needs — including selective and HMO licensing.',
            'Giving notice in the form, and with the period, that the law requires. A message sent through this service is not a legal notice.',
            'Charging only fees you are permitted to charge, and applying rent increases lawfully.',
            'Keeping records for as long as the law requires, and no longer than your lawful basis allows.',
            'Your obligations as a data controller for your tenants’ personal data.',
          ]}
        />
        <p>
          Features that help you keep track — the document checklist, the reminder queue,
          templates, and any wording suggested in the product — are conveniences, not
          compliance. Ticking a document as &ldquo;required&rdquo; is your decision about
          your own paperwork; it is not a statement by us that those documents are the
          right ones, or that they are enough. If you are unsure what applies to you, take
          advice from a solicitor or your local authority.
        </p>
      </Section>

      <Section title="6. Tenancy agreements and signatures">
        <p>
          {product} lets you write or upload a tenancy agreement and lets your tenants agree to
          it by typing their name and the date in the tenant portal, from an account they
          reached with a code sent to their own phone.
        </p>
        <p>
          <strong className="text-ink">This is an ordinary electronic signature, not a
          qualified or witnessed one.</strong> We are not a trust service provider and we do
          not certify anybody&rsquo;s identity. What we record, and what we can evidence, is
          that a particular account signed in and typed a particular name on a particular day,
          together with the exact words shown on the screen at that moment. Whether that is
          sufficient for a given document, in a given country, is a question for you and your
          adviser.
        </p>
        <p>
          The words of your agreement are yours. Any starter text, suggested wording or local
          prompt in the product is a convenience to be edited, never a document to publish
          unread, and it is not legal advice — see section 5.
        </p>
      </Section>

      <Section title="7. Money, and what we do not touch">
        <p>
          {product} never holds, moves, collects or refunds your rent. It records what you and
          your tenants tell it. A payment counts when a person with permission says it arrived
          — a tenant&rsquo;s claim, a declared standing order, or a line from a bank statement
          you have imported does not move a balance on its own.
        </p>
        <p>
          We do not connect to your bank. Importing a statement means you exporting a file and
          uploading it; we have no access to your accounts and cannot initiate anything. Rows
          suggested as matches are suggestions until somebody confirms them.
        </p>
      </Section>

      <Section title="8. Acceptable use">
        <p>You must not use {product} to:</p>
        <List
          items={[
            'Break the law, or infringe anyone’s rights.',
            'Send unsolicited marketing, or messages that harass or intimidate.',
            'Store data you have no lawful basis to hold.',
            'Attempt to gain access to another organisation’s data, or to probe, scan or test the security of the service without our written permission.',
            'Resell or white-label the service without a written agreement.',
          ]}
        />
        <p>
          We may suspend an account that breaches this section. Where the breach is not serious
          we will tell you and give you a reasonable chance to fix it first.
        </p>
      </Section>

      <Section title="9. Your data">
        <p>
          Your data remains yours. We claim no ownership of it and will not use it for any
          purpose other than providing the service to you.
        </p>
        <p>
          You can delete your organisation at any time from Settings. Doing so permanently
          removes your records and your stored documents. Export anything you want to keep
          first — we cannot recover it for you afterwards.
        </p>
      </Section>

      <Section title="10. Cancellation and refunds">
        <p>
          Set out in the <Link to="/refunds">refunds and cancellation policy</Link>, which forms
          part of these terms.
        </p>
      </Section>

      <Section title="11. Liability">
        <p>
          Nothing in these terms limits liability for death or personal injury caused by
          negligence, for fraud, or for anything else that cannot lawfully be limited.
        </p>
        <p>
          Subject to that, our total liability to you in any twelve-month period is limited to
          the fees you paid us in that period. We are not liable for loss of profit, loss of
          business, or loss of data to the extent that loss results from your failure to keep
          your own copies of anything you rely on.
        </p>
        <p>
          The service is provided without warranties beyond those the law implies and does not
          permit us to exclude.
        </p>
      </Section>

      <Section title="12. Ending the agreement">
        <p>
          You may cancel at any time. We may end the agreement on 30 days&rsquo; notice, or
          immediately if you materially breach these terms or fail to pay. If we end it without
          cause we will refund the unused part of your current month.
        </p>
        <p>
          After the agreement ends we keep your data for 30 days so you can ask for it back,
          then delete it.
        </p>
      </Section>

      <Section title="13. Law">
        <p>
          These terms are governed by the law of England and Wales, and the courts of England
          and Wales have exclusive jurisdiction.
        </p>
      </Section>

      <Section title="14. Contact">
        <p>
          {product} is operated by {company}
          {c.company.number ? `, company number ${c.company.number}` : ''}. Correspondence about
          these terms should be addressed to {company}.
        </p>
        {c.company.address && <p>{c.company.address}</p>}
        <p>
          Legal: <a href={`mailto:${c.contact.legal}`}>{c.contact.legal}</a><br />
          Billing: <a href={`mailto:${c.contact.billing}`}>{c.contact.billing}</a><br />
          Support: <a href={`mailto:${c.contact.support}`}>{c.contact.support}</a>
        </p>
      </Section>
    </LegalLayout>
  );
}
